import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Save, CalendarPlus, CircleMinus, CircleCheck, Lock } from "lucide-react";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Skeleton from "../../common/Skeleton";
import Pagination from "../../common/Pagination";
import NoDataFound from "../../common/NoDataFound";
import StatusSwitch from "../../common/StatusSwitch";
import Toast, { type ToastMessage } from "../../common/Toast";
import ApprovalDot, { WorkflowState } from "../../common/ApprovalDot";
import WorkflowModal from "../../common/WorkflowModal";
import { confirmWarning, confirmActive } from "../../common/confirm";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput } from "../../../validation/ValidatedFields";
import { listHolidays, createHoliday, setHolidayActive, materializeWeeklyOffs, type HrStatus } from "../../../services/hrService";
import { Holiday } from "../../../services/hrTypes";

const RBAC_ACTIVITY = "HR_HOLIDAYS";

const isWeeklyOff = (kind?: string) => (kind || "").toUpperCase() === "WEEKLY_OFF";
const today = () => new Date().toISOString().split("T")[0];

const HolidaysAdmin: React.FC = () => {
    const modifyPerm = usePermission(RBAC_ACTIVITY, "view+modify");
    const canModify = modifyPerm.allowed;

    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [statusFilter, setStatusFilter] = useState<HrStatus>("active");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [materializing, setMaterializing] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [calendarId, setCalendarId] = useState(0);
    const [name, setName] = useState("");
    const [holidayDate, setHolidayDate] = useState(today());
    const [isGlobal, setIsGlobal] = useState(false);
    const [saving, setSaving] = useState(false);
    const vh = useFieldValidation();

    const [viewing, setViewing] = useState<Holiday | null>(null);
    const [isActive, setIsActive] = useState(true);
    const [togglingActive, setTogglingActive] = useState(false);
    const [workflowRow, setWorkflowRow] = useState<Holiday | null>(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            setHolidays(await listHolidays(year, statusFilter));
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, [year, statusFilter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { setPage(1); }, [statusFilter, year, pageSize]);

    const paginatedHolidays = useMemo(
        () => holidays.slice((page - 1) * pageSize, page * pageSize),
        [holidays, page, pageSize]
    );

    // Default the calendar id from an existing entity holiday when opening the modal.
    const openCreate = () => {
        const ref = holidays.find((h) => !isWeeklyOff(h.holiday_kind)) || holidays[0];
        setCalendarId(ref?.calendar_id || 0);
        setName(""); setHolidayDate(today()); setIsGlobal(false);
        vh.reset(); setIsOpen(true);
    };

    // Row click → read-only details popup; the Active/Inactive toggle lives inside it.
    const openView = (h: Holiday) => {
        setViewing(h);
        setIsActive(h.is_active !== 0);
    };

    const handleSave = async () => {
        const ok = vh.validateAll([
            { name: "calendarId", code: 202, value: calendarId ? String(calendarId) : "", required: true },
            { name: "name", code: 109, value: name, required: true },
            { name: "holidayDate", code: 305, value: holidayDate, required: true },
        ]);
        if (!ok) return;
        setSaving(true);
        try {
            await createHoliday({
                calendar_id: calendarId,
                holiday_date: holidayDate,
                name: name.trim(),
                holiday_kind: "NATIONAL",
                is_global: isGlobal,
            } as any);
            setToast({ type: "success", message: "Holiday created" });
            setIsOpen(false);
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Save failed" });
        } finally {
            setSaving(false);
        }
    };

    // Active/Inactive lifecycle toggle (is_active 1↔0) — fires immediately from the
    // details popup, separate from soft-delete. Mirrors LeaveRules for consistency.
    const handleToggleActive = async (next: boolean) => {
        if (!viewing) return;
        const confirmFn = next ? confirmActive : confirmWarning;
        const ok = await confirmFn({
            title: next ? "Reactivate this holiday?" : "Inactivate this holiday?",
            text: next ? "It will count as a holiday again." : "It will no longer count as a holiday.",
            confirmText: next ? "Yes, Reactivate" : "Yes, Inactivate",
        });
        if (!ok) return;
        setTogglingActive(true);
        try {
            const updated = await setHolidayActive(viewing.id, next);
            setIsActive(next);
            setViewing(updated);
            setToast({ type: next ? "success" : "warning", message: next ? "Holiday reactivated." : "Holiday inactivated." });
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to update status." });
        } finally {
            setTogglingActive(false);
        }
    };

    const handleMaterialize = async () => {
        setMaterializing(true);
        try {
            await materializeWeeklyOffs(year);
            setToast({ type: "success", message: `Weekly-offs materialized for ${year}` });
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Materialize failed" });
        } finally {
            setMaterializing(false);
        }
    };

    const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "-");

    // Approved state — determines if form is locked
    const isApproved = viewing?.workflow_state === 'approved';

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter === "inactive" ? "INACTIVE" : "ACTIVE"}
                    onChange={(next) => setStatusFilter(next === "INACTIVE" ? "inactive" : "active")}
                    noun="holidays"
                    testId="SEL_holiday_status"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="number"
                        value={String(year)}
                        onChange={(e) => setYear(Number(e.target.value))}
                        className="filter-status"
                        aria-label="Year"
                        data-testid="INP_holiday_year"
                    />
                    <Button
                        variant="secondary-outline"
                        icon={CalendarPlus}
                        disabled={!canModify || materializing}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        onClick={handleMaterialize}
                        data-testid="BTN_holiday_materialize"
                    >
                        {materializing ? "Working..." : "Materialize weekly-offs"}
                    </Button>
                    {canModify && <Button variant="fill" icon={Plus} onClick={openCreate} data-testid="BTN_holiday_create">Publish</Button>}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                <div className="table-list">
                    <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th" style={{ width: 34 }} aria-label="Status" />
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Code</th>
                                <th className="table-list-th">Date</th>
                                <th className="table-list-th">Holiday</th>
                                <th className="table-list-th">Kind</th>
                                <th className="table-list-th">Status</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={["pill", "3xs", "sm", "md", "pill", "pill"]} />}
                            {!loading && holidays.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="table-list-cell">
                                        <NoDataFound title="No holidays" message="Publish a holiday or materialize weekly-offs." />
                                    </td>
                                </tr>
                            )}
                            {!loading && paginatedHolidays.map((h, idx) => (
                                <tr key={h.id} className="table-list-row" data-testid={`ROW_holiday_admin_${h.id}`} onClick={() => openView(h)} style={{ cursor: "pointer" }}>
                                    <td className="table-list-cell" style={{ width: 34 }} onClick={(e) => { e.stopPropagation(); setWorkflowRow(h); }} title="Approval workflow & history" data-testid={`DOT_holiday_status_${h.id}`}>
                                        <ApprovalDot
                                            state={(h.workflow_state || "draft") as WorkflowState}
                                            onClick={(e) => { e.stopPropagation(); setWorkflowRow(h); }}
                                            title="Approval workflow & history"
                                        />
                                    </td>
                                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                    <td className="table-list-cell">{h.code || "—"}</td>
                                    <td className="table-list-cell">{fmtDate(h.holiday_date)}</td>
                                    <td className="table-list-cell"><span className="table-list-cell-title cell-name-lock">{h.workflow_state === 'approved' && (<Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />)}{h.name}</span></td>
                                    <td className="table-list-cell">
                                        <span className={`status-badge xs ${isWeeklyOff(h.holiday_kind) ? "neutral" : "info"}`}>
                                            {isWeeklyOff(h.holiday_kind) ? "Weekly off" : h.holiday_kind}
                                        </span>
                                    </td>
                                    <td className="table-list-cell">
                                        <span className={`status-badge xs ${h.is_active === 0 ? "danger" : "success"}`}>{h.is_active === 0 ? "Inactive" : "Active"}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalRecords={holidays.length}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={[10, 25, 50, 100]}
                        recordLabel="holidays"
                    />
                </div>
                </div>
            </div>

            {/* Create Holiday modal */}
            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm" data-testid="MOD_holiday">
                <Modal.Header>Create Holiday</Modal.Header>
                <Modal.Body>
                    <div className="flex flex-col gap-y-4">
                        <ValidatedInput vh={vh} name="calendarId" code={202} required label="Calendar Id" type="number" value={String(calendarId)} onChange={(e) => setCalendarId(Number(e.target.value))} helper="The holiday calendar this date belongs to." data-testid="INP_holiday_calendar" />
                        <ValidatedInput vh={vh} name="name" code={109} required label="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="INP_holiday_name" />
                        <FormControl.DatePicker
                            label="Date"
                            required
                            value={holidayDate}
                            onChange={(e) => setHolidayDate(e.target.value)}
                            data-testid="INP_holiday_date"
                        />
                        <FormControl.Checkbox label="Global (applies to all entities)" checked={isGlobal} onChange={(c) => setIsGlobal(c)} data-testid="CHK_holiday_global" />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="fill"
                        icon={Save}
                        onClick={handleSave}
                        disabled={!calendarId || !name.trim() || !holidayDate || !canModify || saving}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        data-testid="BTN_holiday_save"
                    >
                        Submit
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Read-only details + Active/Inactive toggle */}
            <Modal isOpen={!!viewing} onClose={() => setViewing(null)} size="sm" data-testid="MOD_holiday_view">
                <Modal.Header>{isApproved ? <>Viewing (locked) "<span className="item-highlight">{viewing?.name}</span>"</> : "Holiday Details"}</Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    <div className="flex flex-col gap-y-3">
                        <div>
                            <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Name</div>
                            <div style={{ fontWeight: 600 }} data-testid="VIEW_holiday_name">{viewing?.name || "-"}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Date</div>
                            <div data-testid="VIEW_holiday_date">{fmtDate(viewing?.holiday_date)}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>Kind</div>
                            <span className={`status-badge xs ${viewing && isWeeklyOff(viewing.holiday_kind) ? "neutral" : "info"}`} data-testid="VIEW_holiday_kind">
                                {viewing && isWeeklyOff(viewing.holiday_kind) ? "Weekly off" : viewing?.holiday_kind}
                            </span>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className={canModify ? "app-modal-footer--split" : ""}>
                    {/* Bottom-left: Active/Inactive lifecycle toggle, but not if approved */}
                    {canModify && !isApproved && (
                        <Button
                            variant={isActive ? "inactive-outline" : "active-outline"}
                            icon={isActive ? CircleMinus : CircleCheck}
                            onClick={() => handleToggleActive(!isActive)}
                            disabled={togglingActive}
                            data-testid="TGL_holiday_active"
                        >
                            {togglingActive ? "..." : isActive ? "Inactive" : "Active"}
                        </Button>
                    )}
                    <Button variant="secondary-outline" onClick={() => setViewing(null)} data-testid="BTN_holiday_view_close">Close</Button>
                </Modal.Footer>
            </Modal>

            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => setWorkflowRow(null)}
                kind="hr_holiday"
                id={workflowRow?.id ?? null}
                onChanged={fetchAll}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default HolidaysAdmin;
